import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../util/auth';
import InviteFriendModal from '../components/InviteFriendModal';
import WebLayout from '../components/WebLayout';
import {
  getFriends,
  getPendingRequests,
  getIncomingRequests,
  acceptFriendRequest,
  rejectFriendRequest,
  removeFriend,
  dismissWalkthrough
} from '../utils/socialActions';
import MobileFriends from '../components/MobileFriends';
import DriverTour from '../components/DriverTour';
import '../styles/driver-custom.css';
import { useNavigate } from 'react-router-dom';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    {...other}
  >
    {value === index && (
      <Box sx={{ py: 3 }}>
        {children}
      </Box>
    )}
  </div>
);

const Friends = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const auth = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [friends, setFriends] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Walkthrough tour state
  const [runFriendsTour, setRunFriendsTour] = useState(false);

  const friendsTourSteps = [
    {
      element: '.friends-tabs-section',
      title: 'Manage Your Friends',
      content: 'Here you can view your friends, pending requests you\'ve sent, and incoming friend requests from others. Switch between tabs to see each category.',
      placement: isMobile ? 'bottom' : 'bottom',
    },
    {
      element: '.friends-table-section',
      title: 'Your Friends List',
      content: 'This is your friends list! You can see each friend\'s total earnings and when they joined. Friends let you compete together on the leaderboard and share your bonus-hunting achievements!',
      placement: isMobile ? 'bottom' : 'top',
    },
    {
      element: '.invite-friend-button',
      title: 'Invite Friends',
      content: 'Click here to invite friends by email. The more friends you add, the more fun the competition becomes! Ready to check out the leaderboard?',
      placement: isMobile ? 'bottom' : 'left',
    },
  ];

  useEffect(() => {
    const loadFriendsData = async () => {
      if (user) {
        try {
          const [friendsData, pendingData, incomingData] = await Promise.all([
            getFriends(user.uid).catch(err => {
              console.error('Error loading friends:', err);
              return [];
            }),
            getPendingRequests(user.uid).catch(err => {
              console.error('Error loading pending requests:', err);
              return [];
            }),
            getIncomingRequests(user.uid).catch(err => {
              console.error('Error loading incoming requests:', err);
              return [];
            })
          ]);
          setFriends(friendsData);
          setPendingRequests(pendingData);
          setIncomingRequests(incomingData);
        } catch (error) {
          console.error('Error loading friends data:', error);
          // Set empty arrays on error
          setFriends([]);
          setPendingRequests([]);
          setIncomingRequests([]);
        }
      }
    };

    loadFriendsData();
  }, [user]);

  // Check if we should continue the walkthrough tour
  useEffect(() => {
    const continueWalkthrough = sessionStorage.getItem('continueWalkthroughFriends');
    console.log('Friends walkthrough check:', { continueWalkthrough, friends: friends.length });

    if (continueWalkthrough === 'true') {
      // Delay to ensure all elements are rendered
      const timer = setTimeout(() => {
        console.log('Starting Friends tour');
        setRunFriendsTour(true);
        // Clear the flag so tour doesn't restart on page refresh
        sessionStorage.removeItem('continueWalkthroughFriends');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [friends]);

  // Handle walkthrough callback
  const handleFriendsTourCallback = async (data) => {
    const { status, action, type, index } = data;

    // Navigate to Leaderboard page when user finishes the friends tour
    const isLastStep = index === friendsTourSteps.length - 1;
    if (type === 'step:after' && isLastStep && action === 'next') {
      // Set flag to continue tour on Leaderboard page
      sessionStorage.setItem('continueWalkthroughLeaderboard', 'true');
      // Navigate to Leaderboard page
      navigate('/leaderboard');
      return;
    }

    // If user skips, dismiss walkthrough immediately
    if (status === 'skipped') {
      setRunFriendsTour(false);
      sessionStorage.removeItem('continueWalkthrough');
      sessionStorage.removeItem('continueWalkthroughFriends');
      sessionStorage.removeItem('continueWalkthroughLeaderboard');
      if (auth.user?.uid) {
        try {
          await dismissWalkthrough(auth.user.uid);
          await auth.refreshUserData();
        } catch (error) {
          console.error('Error dismissing walkthrough:', error);
        }
      }
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      await acceptFriendRequest(requestId);

      // Refresh all lists to ensure UI is up to date
      const [friendsData, pendingData, incomingData] = await Promise.all([
        getFriends(user.uid).catch(err => {
          console.error('Error loading friends:', err);
          return friends; // Keep existing data on error
        }),
        getPendingRequests(user.uid).catch(err => {
          console.error('Error loading pending requests:', err);
          return pendingRequests; // Keep existing data on error
        }),
        getIncomingRequests(user.uid).catch(err => {
          console.error('Error loading incoming requests:', err);
          return incomingRequests; // Keep existing data on error
        })
      ]);

      setFriends(friendsData);
      setPendingRequests(pendingData);
      setIncomingRequests(incomingData);

      // Force refresh of leaderboard data by triggering a window event
      window.dispatchEvent(new CustomEvent('refreshLeaderboard'));
    } catch (error) {
      console.error('Error accepting friend request:', error);
      alert('Failed to accept friend request. Please try again.');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await rejectFriendRequest(requestId);
      const incomingData = await getIncomingRequests(user.uid);
      setIncomingRequests(incomingData);
    } catch (error) {
      console.error('Error rejecting friend request:', error);
      alert('Failed to reject friend request. Please try again.');
    }
  };

  const handleRemoveFriend = async (friendId) => {
    try {
      if (!window.confirm('Are you sure you want to remove this friend?')) {
        return;
      }

      await removeFriend(user.uid, friendId);

      // Refresh friends list
      const friendsData = await getFriends(user.uid);
      setFriends(friendsData);

      // Force refresh of leaderboard data
      window.dispatchEvent(new CustomEvent('refreshLeaderboard'));
    } catch (error) {
      console.error('Error removing friend:', error);
      alert('Failed to remove friend. Please try again.');
    }
  };

  const renderFriendsTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Join Date</TableCell>
            <TableCell>Total Earned</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {friends.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No friends yet. Invite friends to get started!
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            friends.map((friend) => (
              <TableRow key={friend.id}>
                <TableCell>{friend.displayName}</TableCell>
                <TableCell>{friend.email}</TableCell>
                <TableCell>
                  {friend.joinDate ? new Date(friend.joinDate).toLocaleDateString() : 'N/A'}
                </TableCell>
                <TableCell>
                  ${(friend.totalEarned || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={() => handleRemoveFriend(friend.id)}
                    color="error"
                    size="small"
                    title="Remove friend"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderPendingTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Sent Date</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pendingRequests.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No pending requests
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            pendingRequests.map((request) => (
              <TableRow key={request.id}>
                <TableCell>{request.toName || 'N/A'}</TableCell>
                <TableCell>{request.toEmail}</TableCell>
                <TableCell>{new Date(request.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>Pending</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderIncomingTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Received Date</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {incomingRequests.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No incoming requests
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            incomingRequests.map((request) => (
              <TableRow key={request.id}>
                <TableCell>{request.fromName || 'N/A'}</TableCell>
                <TableCell>{request.fromEmail}</TableCell>
                <TableCell>{new Date(request.createdAt).toLocaleDateString()}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <IconButton
                      onClick={() => handleAcceptRequest(request.id)}
                      color="success"
                      size="small"
                    >
                      <CheckIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleRejectRequest(request.id)}
                      color="error"
                      size="small"
                    >
                      <CloseIcon />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const content = (
    <Box
      sx={{
        minHeight: '100vh',
        background: theme.palette.background.default,
        p: isMobile ? 0 : 4,
      }}
    >
      {/* Walkthrough Tour */}
      <DriverTour
        steps={friendsTourSteps}
        run={runFriendsTour}
        onCallback={handleFriendsTourCallback}
        showProgress={true}
        showButtons={['next', 'previous', 'close']}
      />

      {isMobile ? (
        <MobileFriends
          friends={friends}
          pendingRequests={pendingRequests}
          incomingRequests={incomingRequests}
          onAcceptRequest={handleAcceptRequest}
          onRejectRequest={handleRejectRequest}
          onInviteFriend={() => setInviteModalOpen(true)}
          onRemoveFriend={handleRemoveFriend}
        />
      ) : (
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Friends
            </Typography>
            <Button
              className="invite-friend-button"
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setInviteModalOpen(true)}
              sx={{
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                '&:hover': {
                  background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                },
              }}
            >
              Invite Friend
            </Button>
          </Box>

          <Box className="friends-tabs-section" sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                },
              }}
            >
              <Tab label={`Friends (${friends.length})`} />
              <Tab label={`Pending (${pendingRequests.length})`} />
              <Tab label={`Incoming (${incomingRequests.length})`} />
            </Tabs>
          </Box>

          <TabPanel value={activeTab} index={0}>
            <div className="friends-table-section">
              {renderFriendsTable()}
            </div>
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            {renderPendingTable()}
          </TabPanel>
          <TabPanel value={activeTab} index={2}>
            {renderIncomingTable()}
          </TabPanel>
        </Container>
      )}

      <InviteFriendModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
      />
    </Box>
  );

  return isMobile ? content : <WebLayout>{content}</WebLayout>;
};

export default Friends; 